import { IsStringEmpty, isStringNotEmpty, isVarNotEmpty } from './GlobalFunctions'
import { EMPTY_STRING } from '../constants/Common'

/**
 * A set of String utilities.
 */
export class StringUtils {
    public static INTERPOLATION_PLACEHOLDER: string = '{}'

    /**
     * Interpolate a given string.
     * Strings to format should be given with replacement placeholders in the format {}
     *
     * @param str - Initial string to format.
     * @param interpolations - Strings to replace in order.
     *
     * @returns - Interpolated string.
     */
    public static interpolate(str: string, ...interpolations: (Nullable<string> | Nullable<number>)[]): string {
        return interpolations.reduce(
            (accum: string, next: Nullable<string> | Nullable<number>): string =>
                accum?.replace(StringUtils.INTERPOLATION_PLACEHOLDER, `${next}`) ?? EMPTY_STRING,
            str
        )
    }

    /**
     * Split a string at a given index.
     *
     * @param str - String to split.
     * @param index - Index to split at.
     * @returns - Array of two strings.
     */
    public static splitAt(str: string, index: number): [string, string] {
        if (!IsStringEmpty(str) && isVarNotEmpty(index) && str.length > index) {
            return [str.substring(0, index), str.substring(index)]
        }

        return [str.substring(0, str.length), '']
    }

    /**
     * Find the range of the closest surrounding strings to a given index.
     *
     * @param str - String to search.
     * @param index - Index to search around.
     * @param surrounders - Surrounding strings to search for.
     *
     * @returns - Range of the closest surrounding string.
     */
    public static findEntryRange(str: string, index: number, ...surrounders: string[]): [number, number] {
        if (!IsStringEmpty(str) && isVarNotEmpty(index) && surrounders?.length > 0) {
            const rangeSplit: string[] = StringUtils.splitAt(str, index)
            const beforeArray: string[] = rangeSplit[0]?.split('')
            const lastSurrounders: number[] = surrounders
                .map((surrounder: string): number => beforeArray.lastIndexOf(surrounder))
                .filter((surrounderIndex: number): boolean => surrounderIndex > -1)
            const selectStart: number = Math.max(...lastSurrounders) + 1

            const afterArray: string[] = rangeSplit[1]?.split('')
            const firstSurrounders: number[] = surrounders
                .map((surrounder: string): number => afterArray.indexOf(surrounder))
                .filter((surrounderIndex: number): boolean => surrounderIndex > -1)
            const selectEnd: number =
                beforeArray.length + (firstSurrounders.length > 0 ? Math.min(...firstSurrounders) : afterArray.length)

            return [selectStart, selectEnd]
        }

        return [index, index]
    }

    public static inverseSlice = (str: string, start: number, end?: number): string =>
        str.slice(0, start) + (end ? str.slice(end) : '')

    public static replaceRange = (str: string, replacement: string, start: number, end?: number): string =>
        str.substring(0, start) + replacement + (end ? str.substring(end) : EMPTY_STRING)

    public static convertToNumberIfPossible = (value: Nullable<string>): Nullable<number | string> => {
        if (IsStringEmpty(value)) {
            return value
        }

        const normalisedValue: string = value.replace(/^(\d*)x?(\d*)$/, '$1$2')
        const parsedValue: number = Number(isStringNotEmpty(normalisedValue) ? normalisedValue : value)
        return isNaN(parsedValue) ? value : parsedValue
    }

    public static convertCamelCaseToKebabCase = (str: Nullable<string>): Nullable<string> =>
        str?.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2')?.toLowerCase()

    // Replace string array items in string
    public static replaceArrayItemsInString = (str: string, items: string[], replacement: string): string =>
        items.reduce((accum: string, item: string): string => accum.replace(item, replacement), str)
}
